import { AppPageLayout } from "@/components/common/AppPageLayout";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import FormPage from "@/features/pages/components/FormPage";
import { publicFormRoutePaddingClass } from "@/features/pages/components/formPageLayout";
import { buildAnswerMapFromEntryValues } from "@/features/pages/hooks/useFormBranchingNavigation";
import useFormPage from "@/features/pages/hooks/useFormPage";
import { useMsAdlFormPageContext } from "@/features/pages/hooks/useMsAdlFormPageContext";
import { onlyThisEntryValues } from "@/features/pages/utils/onlyThisEntryValues";
import { insertScreenerResponse } from "@/features/screeners/api/insertScreenerResponse";
import { useSelectScreener } from "@/features/screeners/api/selectScreener";
import ScreenerChecking from "@/features/screeners/components/ScreenerChecking";
import ScreenerUnavailable from "@/features/screeners/components/ScreenerUnavailable";
import { parseScreenerSearch, screenerSignupOrganisationId } from "@/features/screeners/utils/screenerSearch";
import { captureError } from "@/lib/sentry";
import { computeCalculatedScores, evaluateScreener, parseScreenerCriteria } from "@patient-watch/forms";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

const ScreenerFormPage = () => {
    const { screenerId, entryId, pageIndex } = Route.useParams();
    const search = Route.useSearch();
    const navigate = useNavigate();
    const { locale } = useAppLocale();
    const [isFinishing, setIsFinishing] = useState(false);
    const resetEntryValues = useEntryValuesStore((state) => state.resetEntryValues);
    const { data: screener, isLoading: isLoadingScreener } = useSelectScreener(screenerId);
    const { data: form } = useSelectFormById(screener?.form_id ?? "", locale);
    const numericPageIndex = Number(pageIndex);

    const {
        currentPage,
        pageCount,
        msAdlSelectionInputId,
        renderPageIndex,
        isLastPageOverride,
        progressPageIndex,
        progressPageTotal,
    } = useMsAdlFormPageContext(form, numericPageIndex);

    const goToPage = (newPageIndex: number) => {
        void navigate({
            to: Route.to,
            params: { screenerId, entryId, pageIndex: String(newPageIndex) },
            search,
            resetScroll: false,
        });
    };

    // Answers stay in the browser until the last page, then the outcome and answers are saved together.
    const finish = async () => {
        if (!screener || isFinishing) return;
        setIsFinishing(true);
        const entryValues = onlyThisEntryValues(useEntryValuesStore.getState().getEntryValues(), entryId);
        const answers = buildAnswerMapFromEntryValues(entryValues);
        // Criteria can check calculated scores too, worked out the same way the database would.
        const scoreInputs = (form?.pages ?? []).flatMap((page) =>
            (page.inputs ?? []).map((input) => ({
                input_id: input.input_id,
                input_key: input.input_key,
                page_index: page.page_index,
            }))
        );
        const scores = computeCalculatedScores(
            screener.forms?.score_engine,
            screener.forms?.score_engine_config,
            scoreInputs,
            answers
        );
        const outcome = evaluateScreener(parseScreenerCriteria(screener.criteria), { ...answers, ...scores });

        // The sign-up link carries this quickstart_id, so the answers are saved onto the diary sign-up creates.
        const quickstartId = crypto.randomUUID();
        try {
            await insertScreenerResponse({
                screenerId,
                outcome,
                answers: { ...answers, ...scores },
                entryValues,
                organisationId: screenerSignupOrganisationId(search.signup),
                quickstartId,
            });
        } catch (error) {
            // A failed save should not stop the patient seeing their result.
            captureError(error instanceof Error ? error : new Error(String(error)), { screenerId });
        }

        resetEntryValues();
        void navigate({
            to: "/screen/$screenerId/result",
            params: { screenerId },
            search: { ...search, outcome, quickstart_id: quickstartId },
            replace: true,
        });
    };

    const {
        form: reactHookForm,
        onSubmit,
        prevPage,
        nextPage,
        isLastPage,
        visitedPageCount,
    } = useFormPage({
        pageCount,
        inputs: currentPage?.inputs ?? [],
        nextEntry: null,
        form,
        formType: "tracker",
        isLastPageOverride,
        skipDbWrite: true,
        entryId,
        nextPageFn: goToPage,
        prevPageFn: goToPage,
        successFn: () => void finish(),
        completeFn: () => void finish(),
    });

    if (isFinishing) return <ScreenerChecking />;

    if (!isLoadingScreener && !screener) return <ScreenerUnavailable />;
    if (!form || !currentPage) return null;

    return (
        <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
            <FormPage
                pageIndex={numericPageIndex}
                form={form}
                route={Route}
                formType="tracker"
                reactHookForm={reactHookForm}
                onSubmit={onSubmit}
                prevPage={prevPage}
                nextPage={nextPage}
                isLastPage={isLastPageOverride ?? isLastPage}
                visitedPageCount={visitedPageCount}
                msAdlSelectionInputId={msAdlSelectionInputId}
                renderPageIndex={renderPageIndex}
                progressPageIndex={progressPageIndex}
                progressPageTotal={progressPageTotal}
            />
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/screen/$screenerId/$entryId/page/$pageIndex")({
    component: ScreenerFormPage,
    validateSearch: parseScreenerSearch,
});
